import React, { ReactNode, useCallback, useState } from 'react';
import styled from 'styled-components';
import { BodySmall } from '@shared/ui/atoms/fonts/Body';
import { Center } from '@shared/ui/atoms/StructuralLayout';
import { SimpleSpinner } from '@shared/ui/atoms/SimpleSpinner';
import { HoverPopup } from '@shared/ui/molecules/HoverPopup';
import { getWhereaboutsInfo, WhereaboutsDayInfo } from '@features/whereabouts/services/WhereaboutsInfoService';

interface Props {
  trigger: ReactNode;
  noteUserId?: string;
  noteDates?: string[];
  className?: string;
  style?: React.CSSProperties;
  controlledOpen?: boolean;
}

export function CellInfoHover({ trigger, noteUserId, noteDates, className, style, controlledOpen }: Props) {
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState<WhereaboutsDayInfo[]>([]);
  const [fetched, setFetched] = useState(false);

  const hasNote = !!(noteUserId && noteDates && noteDates.length > 0);

  const fetchNotes = useCallback(async () => {
    if (!hasNote || fetched || loading) return;
    setLoading(true);
    try {
      const responses = await Promise.all(noteDates!.map(d => getWhereaboutsInfo(d, noteUserId!)));
      setNotes(responses.filter(r => r.title || r.label));
      setFetched(true);
    } finally {
      setLoading(false);
    }
  }, [hasNote, noteDates, noteUserId, loading, fetched]);

  if (!hasNote) {
    if (style || className) {
      return <div className={className} style={style}>{trigger}</div>;
    }
    return <>{trigger}</>;
  }

  return (
    <HoverPopup
      className={className}
      style={style}
      delayMs={500}
      onOpen={fetchNotes}
      controlledOpen={controlledOpen}
      trigger={trigger}
      popup={
        loading
          ? <Center><SimpleSpinner size={22} /></Center>
          : <>
              {notes.map((info, i) => (
                <Entry key={i}>
                  <BodySmall weight={600}>{info.title}</BodySmall>
                  <BodySmall>{info.label}</BodySmall>
                </Entry>
              ))}
            </>
      }
    />
  );
}

const Entry = styled.div`
  padding: 4px 0;
`;
